import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { Radio as AriaRadioButton } from 'react-aria-components';
import type { RadioProps } from 'react-aria-components';

import { useFusionTheme } from '../hooks';

import styles from './RadioButton.module.scss';

export type Props = Pick<
  RadioProps,
  | 'value'
  | 'isDisabled'
  | 'className'
  | 'onFocus'
  | 'onBlur'
  | 'onFocusChange'
  | 'slot'
  | 'id'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-describedby'
  | 'aria-details'
> &
  VariantProps<typeof radioVariants> & {
    subtext?: string;
    error?: string;
    children?: ReactNode;
  };

const radioVariants = cva(styles.container, {
  variants: {
    size: {
      small: styles.sizeSmall,
      medium: styles.sizeMedium
    }
  }
});

const RadioButton = ({ size = 'medium', subtext, error, children, className, ...props }: Props) => {
  const { addThemeClassName } = useFusionTheme();
  const hasError = !!error;

  return (
    <AriaRadioButton
      data-invalid={hasError || null}
      {...props}
      className={addThemeClassName(radioVariants({ size }), className, {
        [styles.invalid]: hasError
      })}
    >
      <div className={styles.radioContainer}>
        <div className={styles.radio}>
          <div className={styles.outline} />
        </div>
        <div className={styles.labels}>
          <div className={styles.label}>{children}</div>
          {subtext && !hasError && <div className={styles.subtitle}>{subtext}</div>}
        </div>
      </div>
      {hasError && <div className={styles.errorLabel}>{error}</div>}
    </AriaRadioButton>
  );
};

export default RadioButton;
